import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { RefreshCw, X } from 'lucide-react'
import { Button } from './ui'

/**
 * Hur ofta en öppen flik letar efter en ny version.
 *
 * Det var en gång i timmen, och i adminytan märktes det som att rutan aldrig
 * kom. Där står fliken öppen i timmar och man byter sida utan att sidan
 * laddas om – så en ny version upptäcktes först långt efter att den
 * publicerats. Frågan är liten, bara service workerns egen fil, så fem
 * minuter kostar ingenting.
 */
const UPDATE_CHECK_MS = 5 * 60 * 1000

/**
 * Tätast så här ofta, oavsett hur många vägar som ber om en kontroll.
 *
 * Sidbyten, att fliken blir synlig och intervallet kan alla fyra inom samma
 * sekund. En gång i minuten räcker för att en ny version ska synas medan man
 * arbetar.
 */
const CHECK_THROTTLE_MS = 60 * 1000

/**
 * Hur länge vi väntar på att den nya service workern ska ta över innan vi
 * tvingar fram omladdningen själva.
 *
 * Övertagandet brukar ta under 100 ms. Två och en halv sekund är alltså inte
 * en gissning på hur lång tid det tar, utan hur länge man orkar titta på en
 * knapp som ser ut att ha hängt sig.
 */
const TAKEOVER_TIMEOUT_MS = 2500

/**
 * Erbjuder användaren att hämta en ny version av appen.
 *
 * Utan det här fastnar folk på den version de först öppnade – service workern
 * installerar visserligen nya filer, men tar dem inte i bruk förrän alla
 * flikar stängts, och en app som ligger på hemskärmen stängs sällan. Vi
 * frågar i stället för att ladda om automatiskt: att byta version mitt under
 * en inlösen i kassan vore en dålig upplevelse.
 *
 * Omladdningen sköts med hängslen och livrem, för att knappen inte ska kunna
 * bli verkningslös. `updateServiceWorker` skickar bara ett meddelande till den
 * väntande service workern och litar på att biblioteket fångar upp när den
 * tagit över – gör den inte det händer ingenting alls, och rutan blir kvar
 * medan användaren klickar om och om igen. Det var precis det som hände.
 */
export function UpdatePrompt() {
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null)
  const lastCheck = useRef(0)

  /** Frågar efter en ny version, högst en gång per minut och bara när någon tittar. */
  const checkForUpdate = useCallback(() => {
    const registration = registrationRef.current
    if (!registration || document.visibilityState !== 'visible') return
    const now = Date.now()
    if (now - lastCheck.current < CHECK_THROTTLE_MS) return
    lastCheck.current = now
    // Utan nät misslyckas frågan, och det är inget fel – vi frågar igen sen.
    registration.update().catch(() => undefined)
  }, [])

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (!registration) return
      registrationRef.current = registration
      lastCheck.current = Date.now()
      // En flik som stått öppen i en vecka ska ändå upptäcka nya versioner.
      window.setInterval(checkForUpdate, UPDATE_CHECK_MS)
    },
  })

  // När man kommer tillbaka till fliken, är det där man ska få veta.
  useEffect(() => {
    document.addEventListener('visibilitychange', checkForUpdate)
    window.addEventListener('focus', checkForUpdate)
    return () => {
      document.removeEventListener('visibilitychange', checkForUpdate)
      window.removeEventListener('focus', checkForUpdate)
    }
  }, [checkForUpdate])

  // Ett sidbyte laddar inte om sidan, så det måste fråga själv.
  const { pathname } = useLocation()
  useEffect(() => {
    checkForUpdate()
  }, [pathname, checkForUpdate])

  const [busy, setBusy] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const reloaded = useRef(false)

  /*
   * En stängd ruta gäller den versionen, inte alla framtida.
   *
   * Den som tryckte "Inte nu" en gång fick aldrig mer veta att något nytt
   * fanns under samma besök – inte ens när ytterligare en version kom. Nu
   * kommer rutan tillbaka när en service worker som inte redan setts står
   * och väntar.
   */
  const seenWaiting = useRef<ServiceWorker | null>(null)
  useEffect(() => {
    if (!dismissed) return
    const interval = window.setInterval(() => {
      const waiting = registrationRef.current?.waiting ?? null
      if (waiting && seenWaiting.current && waiting !== seenWaiting.current) {
        setDismissed(false)
      }
    }, CHECK_THROTTLE_MS)
    return () => window.clearInterval(interval)
  }, [dismissed])

  /** Omladdning får ske en gång, oavsett hur många vägar som når hit. */
  function reloadOnce() {
    if (reloaded.current) return
    reloaded.current = true
    window.location.reload()
  }

  async function handleUpdate() {
    setBusy(true)

    // Egen lyssnare, oberoende av bibliotekets. Den fyrar så fort den nya
    // service workern tagit över sidan, vilket är det snabbaste läget att
    // ladda om i.
    navigator.serviceWorker?.addEventListener('controllerchange', reloadOnce, { once: true })

    /*
     * Knacka på direkt, innan bibliotekets väg provas.
     *
     * `updateServiceWorker` importerar workbox-window dynamiskt och gör
     * ingenting alls om den importen misslyckas – den sväljer felet och
     * returnerar som om allt gått bra. Meddelandet här går rakt till den
     * väntande service workern utan mellanhänder.
     */
    const registration = await navigator.serviceWorker?.getRegistration()
    registration?.waiting?.postMessage({ type: 'SKIP_WAITING' })

    try {
      await updateServiceWorker(true)
    } catch {
      // Struntsamma – omladdningen nedan sker ändå.
    }

    // Sista utvägen. Bättre en omladdning som inte behövdes än en knapp som
    // ser trasig ut.
    window.setTimeout(reloadOnce, TAKEOVER_TIMEOUT_MS)
  }

  if (!needRefresh || dismissed) return null

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-24 sm:pb-6"
      role="status"
    >
      <div className="flex w-full max-w-md items-center gap-3 rounded-card border-2 border-accent-strong bg-accent px-4 py-3 text-on-accent shadow-lift">
        <RefreshCw
          size={20}
          className={busy ? 'shrink-0 animate-spin' : 'shrink-0'}
          aria-hidden
        />
        <p className="flex-1 text-sm font-semibold">
          {busy ? 'Hämtar nya versionen …' : 'En ny version av häftet finns.'}
        </p>
        {!busy && (
          <>
            <Button size="sm" variant="primary" onClick={() => void handleUpdate()}>
              Uppdatera
            </Button>
            <button
              type="button"
              onClick={() => {
                seenWaiting.current = registrationRef.current?.waiting ?? null
                setDismissed(true)
              }}
              aria-label="Inte nu"
              className="-mr-1 flex size-11 shrink-0 items-center justify-center rounded-pill opacity-70 hover:opacity-100"
            >
              <X size={18} aria-hidden />
            </button>
          </>
        )}
      </div>
    </div>
  )
}
